import { useEffect, useRef, useState } from "react";
import {
  COMPLETION_ITEM_LABEL,
  COMPLETION_ITEMS,
  COMPLETION_OUTCOME_CHOICE_LABEL,
  COMPLETION_OUTCOMES,
  CUSTOM_COMPLETION_HEADING_MAX,
  type CompletionHint,
  type CompletionItem,
  type CompletionOutcome,
  type CompletionRound,
} from "@care-by-ella/contracts";
import {
  fetchCompletionHints,
  fetchMedicalEvidence,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  completionItemLine,
  completionProgressFor,
  currentRound,
} from "@/lib/completion-round";

// TES-950 (TES-941 §BESLUT OCH KOMPLETTERINGAR) — the ONE completion picker,
// used after a care meeting, in the prescription-transfer review and on a
// reviewed lab result. Exactly five options, no fixed packages; beside each,
// what is already on file (computed on the server) so Kirill sees what is
// missing before he picks. What the options become for the patient is the
// contract's job (`completion-picker.ts`), never this component's.

export interface CompletionValue {
  // Null until the doctor has chosen — the meeting form will not submit then.
  outcome: CompletionOutcome | null;
  items: CompletionItem[];
  // TES-1000 (Ö-246 (b)) — "Annan specificerad komplettering" is a row of
  // her checklist: his patient-friendly heading, the instruction, and
  // whether she may upload a file to it.
  otherHeading: string;
  otherText: string;
  otherUploadAllowed: boolean;
}

export const EMPTY_COMPLETION: CompletionValue = {
  outcome: null,
  items: [],
  otherHeading: "",
  otherText: "",
  otherUploadAllowed: false,
};

// The value as the API takes it: items only on "Komplettering behövs", the
// heading, instruction and upload choice only with "Annan specificerad
// komplettering".
export function completionPayload(value: CompletionValue) {
  const items = value.outcome === "completion" ? value.items : [];
  if (!items.includes("other")) return { items };
  return {
    items,
    otherHeading: value.otherHeading.trim(),
    otherText: value.otherText.trim(),
    ...(value.otherUploadAllowed ? { otherUploadAllowed: true } : {}),
  };
}

// Why the picker cannot be submitted yet, in the doctor's words; null when it
// can. The API refuses the same things.
export function completionProblem(value: CompletionValue): string | null {
  if (value.outcome === null) return "Välj ett beslut.";
  if (value.outcome !== "completion") return null;
  if (value.items.length === 0) return "Välj minst en komplettering.";
  if (value.items.includes("other") && !value.otherHeading.trim())
    return "Ge den andra kompletteringen en rubrik.";
  if (value.items.includes("other") && !value.otherText.trim())
    return "Beskriv den andra kompletteringen.";
  return null;
}

export function CompletionPicker({
  patientId,
  value,
  onChange,
  showOutcome = true,
  showItems = true,
  // Items the entry point has already decided: on the lab review, a new
  // sample is a new blood test by definition.
  requiredItems = [],
  recommendationNotes = {},
  disabled = false,
  onUnauthorized,
}: {
  patientId: string;
  value: CompletionValue;
  onChange: (next: CompletionValue) => void;
  // False where the entry point asks the outcome with its own buttons (the
  // transfer review, the lab review): the picker is then the komplettering
  // list alone.
  showOutcome?: boolean;
  // TES-1316 — false in the care meeting's step 3, which asks the outcome
  // alone; the komplettering list is step 4's.
  showItems?: boolean;
  requiredItems?: CompletionItem[];
  // TES-1330 — the meeting view's "Rekommenderade kompletteringar", as a
  // line beside the option each concerns. Information only: it never ticks
  // the option (Ö-244 (b)), so nothing reaches the patient unless he does.
  recommendationNotes?: Partial<Record<CompletionItem, string>>;
  disabled?: boolean;
  onUnauthorized: () => void;
}) {
  const [hints, setHints] = useState<CompletionHint[] | null>(null);
  const [hintError, setHintError] = useState(false);
  // A ref, not a dependency: an inline callback would re-fetch on every render.
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    fetchCompletionHints(patientId)
      .then((h) => {
        if (live) setHints(h.hints);
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current();
          return;
        }
        // Without hints the picker still works; the doctor just does not get
        // the "saknas" line. Never block a decision on a helper.
        if (live) setHintError(true);
      });
    return () => {
      live = false;
    };
  }, [patientId]);

  const hintFor = (item: CompletionItem) =>
    hints?.find((h) => h.item === item) ?? null;

  // TES-1000 (Ö-244 (b)) — nothing is ticked for him from what is missing:
  // the server sends `preselect: false` for every hint, and the only seed
  // left is what the entry point itself requires. Applied when the
  // komplettering list OPENS with nothing chosen yet, never over a choice
  // already made.
  const preselected = (): CompletionItem[] => {
    const fromHints = (hints ?? [])
      .filter((h) => h.preselect)
      .map((h) => h.item);
    return COMPLETION_ITEMS.filter(
      (i) => fromHints.includes(i) || requiredItems.includes(i),
    );
  };

  const listOpen =
    showItems && (!showOutcome || value.outcome === "completion");
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!listOpen || seeded || (hints === null && !hintError)) return;
    setSeeded(true);
    if (value.items.length === 0) {
      const initial = preselected();
      if (initial.length > 0) onChange({ ...value, items: initial });
    }
    // Seeding is a one-shot on open: the value is read here, not tracked.
  }, [listOpen, seeded, hints, hintError]);

  function chooseOutcome(outcome: CompletionOutcome) {
    if (outcome === value.outcome) return;
    if (outcome !== "completion") setSeeded(false);
    onChange({ ...value, outcome });
  }

  function toggle(item: CompletionItem) {
    if (requiredItems.includes(item)) return;
    const items = value.items.includes(item)
      ? value.items.filter((i) => i !== item)
      : COMPLETION_ITEMS.filter((i) => i === item || value.items.includes(i));
    onChange({ ...value, items });
  }

  return (
    <div className="flex flex-col gap-sm">
      {showOutcome ? (
        <fieldset>
          <legend className="mb-xs block text-xs font-semibold text-textSecondary">
            Beslut efter granskning
          </legend>
          <div className="flex flex-col gap-xs">
            {COMPLETION_OUTCOMES.map((outcome) => {
              const selected = value.outcome === outcome;
              return (
                <label
                  key={outcome}
                  className={`flex cursor-pointer items-center gap-sm rounded-md border p-sm text-sm transition ${
                    selected
                      ? "border-brand bg-brand-accent"
                      : "border-surfaceMuted bg-surface hover:border-brand/40"
                  }`}
                >
                  <input
                    type="radio"
                    name={`completion-outcome-${patientId}`}
                    className="accent-brand"
                    checked={selected}
                    disabled={disabled}
                    onChange={() => chooseOutcome(outcome)}
                  />
                  <span className="font-semibold text-textPrimary">
                    {COMPLETION_OUTCOME_CHOICE_LABEL[outcome]}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {listOpen ? (
        <fieldset className="flex flex-col gap-xs">
          <legend className="mb-xs block text-xs font-semibold text-textSecondary">
            Vad behöver patienten komplettera?
          </legend>
          {COMPLETION_ITEMS.map((item) => {
            const hint = hintFor(item);
            const required = requiredItems.includes(item);
            const checked = value.items.includes(item);
            return (
              <div key={item} className="flex flex-col">
                <label className="flex cursor-pointer items-start gap-sm text-sm text-textPrimary">
                  <input
                    type="checkbox"
                    className="accent-brand mt-[3px]"
                    checked={checked}
                    disabled={disabled || required}
                    onChange={() => toggle(item)}
                  />
                  <span className="min-w-0">
                    {COMPLETION_ITEM_LABEL[item]}
                    {recommendationNotes[item] ? (
                      <span className="block text-xs font-semibold text-textPrimary">
                        {recommendationNotes[item]}
                      </span>
                    ) : null}
                    {hint?.hint ? (
                      <span
                        className={`block text-xs ${
                          hint.present === false
                            ? "text-warning"
                            : "text-textSecondary"
                        }`}
                      >
                        {hint.hint}
                      </span>
                    ) : null}
                  </span>
                </label>
                {item === "other" && checked ? (
                  <div className="ml-lg mt-xs flex flex-col gap-xs">
                    <input
                      type="text"
                      value={value.otherHeading}
                      onChange={(e) =>
                        onChange({ ...value, otherHeading: e.target.value })
                      }
                      maxLength={CUSTOM_COMPLETION_HEADING_MAX}
                      disabled={disabled}
                      aria-label="Rubrik för patienten"
                      placeholder="Rubrik som patienten ser, t.ex. ”Intyg från vårdcentralen”"
                      className="rounded-md border border-surfaceMuted bg-surface p-sm text-sm focus:border-brand focus:outline-none"
                    />
                    <textarea
                      value={value.otherText}
                      onChange={(e) =>
                        onChange({ ...value, otherText: e.target.value })
                      }
                      rows={2}
                      disabled={disabled}
                      aria-label="Instruktion till patienten"
                      placeholder="Vad ska patienten göra? Skriv så att hon förstår utan att fråga."
                      className="rounded-md border border-surfaceMuted bg-surface p-sm text-sm focus:border-brand focus:outline-none"
                    />
                    <label className="flex cursor-pointer items-center gap-sm text-xs text-textSecondary">
                      <input
                        type="checkbox"
                        className="accent-brand"
                        checked={value.otherUploadAllowed}
                        disabled={disabled}
                        onChange={(e) =>
                          onChange({
                            ...value,
                            otherUploadAllowed: e.target.checked,
                          })
                        }
                      />
                      Patienten får ladda upp en fil till uppgiften
                    </label>
                  </div>
                ) : null}
              </div>
            );
          })}
          {hintError ? (
            <p className="text-xs text-textSecondary">
              Kunde inte läsa vad som redan finns — välj utifrån underlaget.
            </p>
          ) : null}
          <p className="text-xs text-textSecondary">
            Patienten får bara det du väljer, samlat under ”Komplettera ditt
            medicinska underlag”.
          </p>
        </fieldset>
      ) : null}
    </div>
  );
}

// "Kompletteringar: n av m klara" for the patient's open completion, and
// (TES-1059) under it each requested part with its source and time, so a
// partial komplettering is visible in the case before the round is ready.
// Read-only: the admin support view renders the same thing.
export function CompletionProgress({
  patientId,
  reloadKey = 0,
  onUnauthorized,
}: {
  patientId: string;
  reloadKey?: number;
  onUnauthorized: () => void;
}) {
  const [label, setLabel] = useState<string | null>(null);
  const [round, setRound] = useState<CompletionRound | null>(null);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  useEffect(() => {
    let live = true;
    fetchMedicalEvidence(patientId)
      .then((d) => {
        if (!live) return;
        setLabel(completionProgressFor(d));
        setRound(currentRound(d));
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) onUnauthorizedRef.current();
        // A missing counter is not worth an error banner.
      });
    return () => {
      live = false;
    };
  }, [patientId, reloadKey]);
  if (!label) return null;
  return (
    <div className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-xs">
      <p className="font-semibold text-textPrimary">{label}</p>
      {round && round.items.length > 0 ? (
        <ul
          className="mt-xs flex flex-col gap-xs"
          aria-label="Kompletteringshistorik"
        >
          {round.items.map((i) => (
            <li
              key={i.id}
              className="flex flex-wrap items-baseline gap-x-sm text-textSecondary"
            >
              <span className="text-textPrimary">
                {completionItemLine(i).label}
              </span>
              <span>{completionItemLine(i).state}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
